import { AlertCircle, Eye, EyeOff, Loader2 } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAppDispatch, useAppSelector } from '@/store/hooks';

import { clearError, loginUser, selectAuth } from '../authSlice';

interface LocationState {
  from?: { pathname?: string };
}

/**
 * Only same-app relative paths survive — `//host`, absolute URLs and empty
 * values are dropped so a `?returnTo=` can never become an open redirect.
 */
function sanitizeReturnTo(value: string | null): string | null {
  if (!value || !value.startsWith('/') || value.startsWith('//')) {
    return null;
  }
  return value;
}

/**
 * Sign-in (design §7). Accounts are issued in admin-ui — there is deliberately
 * no registration and no password-recovery link here; the static helper line
 * under the form sends the user to their administrator instead.
 */
export function LoginPage() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, isLoading, error, sessionExpired } = useAppSelector(selectAuth);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const from =
    (location.state as LocationState | null)?.from?.pathname ??
    sanitizeReturnTo(new URLSearchParams(location.search).get('returnTo')) ??
    '/';

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    void signIn();
  }

  async function signIn() {
    const result = await dispatch(loginUser({ email, password }));
    if (loginUser.fulfilled.match(result)) {
      navigate(from, { replace: true });
    }
  }

  // A forced logout (failed refresh) explains itself instead of a bare form.
  const notice = error ?? (sessionExpired ? 'Your session expired. Please sign in again.' : null);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-6 text-foreground">
      <div className="w-full max-w-sm py-12">
        <p className="text-base font-semibold">Workspace</p>

        <h1 className="mt-8 text-2xl font-semibold tracking-tight">Sign in</h1>
        <p className="mt-1 text-sm text-muted-foreground">Use your work email and password.</p>

        {notice && (
          <div
            id="login-error"
            role="alert"
            className="mt-6 flex items-start gap-2 rounded-md bg-muted px-3 py-2 text-sm"
          >
            {error && (
              <AlertCircle
                className="mt-0.5 h-4 w-4 shrink-0 text-destructive"
                aria-hidden="true"
              />
            )}
            <span>{notice}</span>
          </div>
        )}

        <form className="mt-6 space-y-4" onSubmit={handleSubmit}>
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              placeholder="you@company.com"
              required
              disabled={isLoading}
              value={email}
              onChange={(event) => {
                setEmail(event.target.value);
                if (error) {
                  dispatch(clearError());
                }
              }}
              aria-invalid={error ? true : undefined}
              aria-describedby={notice ? 'login-error' : undefined}
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="password">Password</Label>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={() => setShowPassword((value) => !value)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? (
                  <EyeOff className="h-4 w-4" aria-hidden="true" />
                ) : (
                  <Eye className="h-4 w-4" aria-hidden="true" />
                )}
              </Button>
            </div>
            <Input
              id="password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              placeholder="Enter your password"
              required
              disabled={isLoading}
              value={password}
              onChange={(event) => {
                setPassword(event.target.value);
                if (error) {
                  dispatch(clearError());
                }
              }}
              aria-describedby={notice ? 'login-error' : undefined}
            />
          </div>

          <Button type="submit" className="h-11 w-full" disabled={isLoading}>
            {isLoading ? (
              <>
                <Loader2
                  className="mr-2 h-4 w-4 animate-spin motion-reduce:animate-none"
                  aria-hidden="true"
                />
                Signing in…
              </>
            ) : (
              'Sign in'
            )}
          </Button>
        </form>

        <p className="mt-6 text-sm text-muted-foreground">
          No account? Ask your administrator to create one for you.
        </p>
      </div>
    </div>
  );
}
